import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/site/FormBits";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { useSeo } from "@/lib/seo";

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  useSeo({ title: isLogin ? "Student sign in" : "Create your student account", path: isLogin ? "/login" : "/signup", noindex: true });
  const [params] = useSearchParams();
  const next = params.get("next");
  const { user, login, register } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: params.get("email") || "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  if (user) return <Navigate to={user.role === "host" ? "/host" : next || "/dashboard"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = isLogin ? await login(f.email, f.password) : await register(f.name, f.email, f.password);
      nav(u.role === "host" ? "/host" : next || (isLogin ? "/dashboard" : "/apply"), { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  const swap = `${isLogin ? "/signup" : "/login"}${params.toString() ? `?${params}` : ""}`;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-8 py-14 sm:py-20 grid lg:grid-cols-2 gap-12 items-center">
      <div className="hidden lg:block">
        <img src="/brand/esc-clubhouse.jpg" alt="Esc Digital Nomad Village clubhouse at dusk, Popoyo, Nicaragua" className="rounded-3xl h-[460px] w-full object-cover" />
      </div>
      <div className="max-w-md w-full mx-auto">
        <p className="font-mono-ui text-xs tracking-[0.3em] text-[#1A9AA8]">STUDENT PORTAL</p>
        <h1 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-[#0B2A4A]">{isLogin ? "Welcome back" : "Create your student account"}</h1>
        <p className="mt-3 text-slate-600">{isLogin ? "Sign in to continue your course, worksheets, and messages." : "Use the same email you used on the questionnaire so your profile connects."}</p>
        <form onSubmit={submit} className="mt-8 flex flex-col gap-5" data-testid={isLogin ? "login-form" : "signup-form"}>
          {!isLogin && (
            <Field label="Full name" htmlFor="auth-name">
              <Input id="auth-name" required value={f.name} onChange={set("name")} data-testid="auth-name-input" />
            </Field>
          )}
          <Field label="Email" htmlFor="auth-email">
            <Input id="auth-email" type="email" required value={f.email} onChange={set("email")} data-testid="auth-email-input" />
          </Field>
          <Field label="Password" htmlFor="auth-password" hint={isLogin ? null : "At least 8 characters"}>
            <Input id="auth-password" type="password" required minLength={isLogin ? 1 : 8} value={f.password} onChange={set("password")} data-testid="auth-password-input" />
          </Field>
          <button disabled={busy} className="rounded-xl bg-[#0B2A4A] text-[#F5ECD7] py-3.5 font-semibold hover:bg-[#071C33] disabled:opacity-60" data-testid="auth-submit-btn">
            {busy ? "Please wait..." : isLogin ? "Sign in" : "Create account"}
          </button>
        </form>
        <p className="mt-6 text-sm text-slate-600">
          {isLogin ? "New here? " : "Already have an account? "}
          <Link to={swap} className="font-semibold text-[#F08A3C]" data-testid="auth-switch-link">{isLogin ? "Create an account" : "Sign in"}</Link>
        </p>
      </div>
    </section>
  );
}
